import * as THREE from "three";
import {AudioLoader, PositionalAudio} from "three";
import {type Dispatch, type SetStateAction} from "react";
import type {SourceState} from "../Sources.tsx";
import {type AudioPlayer} from "./AudioPlayer";
import Ramp from "./ramp/Ramp";
import {ensureSilentAudioKeeper, isSafari} from "./SafariPlayerHelper";

export default class ThreePlayer implements AudioPlayer {

    private readonly url: string;
    private readonly setState: Dispatch<SetStateAction<SourceState>>
    readonly audio: PositionalAudio;
    readonly audioNode: THREE.Object3D
    private readonly scene: THREE.Scene;

    private readonly ramp: Ramp;
    private volumeSetting = 0;

    constructor(scene: THREE.Scene, camera: THREE.Camera, url: string, setState: Dispatch<SetStateAction<SourceState>>) {
        this.scene = scene;
        this.url = url;
        this.setState = setState;

        const listener = new THREE.AudioListener();
        camera.add(listener);
        this.audio = new PositionalAudio(listener);
        this.audioNode = new THREE.Object3D();

        this.audio.onEnded = () => {
            console.trace("ended");
            this.audio.stop();
            this.setState("ready");
        };

        this.ramp = new Ramp((volume) => {
            //console.debug("ramp volume to " + volume.toFixed(3));
            this.audio.setVolume(volume);
        });
    }

    load = (loaded: (success: boolean) => void) => {
        this.setState("loading");
        // load a sound and set it as the {@link PositionalAudio} object's buffer
        const audioLoader = new AudioLoader();
        audioLoader.load(this.url,
            (buffer) => {
                this.audio.setBuffer(buffer);
                this.audio.setRefDistance(1);
                this.audioNode.add(this.audio);
                this.scene.add(this.audioNode);
                this.audio.panner.panningModel = "HRTF";
                this.audio.panner.distanceModel = "linear";
                this.setState("ready");
                loaded(true);
            },
            undefined,
            (error) => {
                console.error("Audio loading error:", error);
                this.setState("error");
                loaded(false);
            });
    }

    loop = () => {
        this.audio.onEnded = () => {
            //console.trace("looping");
            this.audio.stop();
            this.audio.play();
        };
    }

    toggle = () => {
        //console.trace("three player - toggle playback");
        if (this.audio.isPlaying) {
            this.pause();
            return;
        }

        // iOS: start silent audio keeper to keep audio path open (must be in user gesture)
        if (isSafari()) {
            ensureSilentAudioKeeper();
        }
        this.play();
    }

    volume = (dB: number) => {
        //console.debug("Attenuate by " + dB.toFixed(1) + " dB.");
        const value = Math.pow(10, (dB / 20));
        // change the volume of audio only during playback and not triggered by pure playback state changes
        if (this.audio.isPlaying && this.volumeSetting !== value) {
            this.audio.setVolume(value);
        }
        // this is what the user sees
        this.volumeSetting = value;
    }

    pan = (horizontal: number, vertical: number) => {
        //console.trace("pan horizontal to " + horizontal.toFixed(2) + ", vertical to " + vertical.toFixed(2));
        this.audioNode.position.set(horizontal * 50, vertical * 50, 0);
    }

    private play() {
        this.audio.play();
        this.ramp.on(this.volumeSetting).then(() => {
            // ramp complete
        });
        this.setState("running");
    }
    private pause() {
        this.ramp.off(this.volumeSetting).then(() => {
                this.audio.pause();
                this.setState("ready");
            }
        );
    }

    // TODO when to call this?
    cleanup = () => {
        if (this.audio.isPlaying) {
            this.audio.stop();
        }
        // this.camera.remove(listener); // todo: remove listener from camera
        this.scene.remove(this.audioNode);
    }

}